import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, LogOut, RefreshCcw, Filter, Search, Calendar, Package, MessageCircle, Phone, Mail, MapPin, ChevronRight, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { buildWhatsAppUrl } from "@/lib/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mo_admin_token";

const STATUS_STYLES = {
  new: "bg-blue-50 text-blue-700 border-blue-200",
  contacted: "bg-amber-50 text-amber-700 border-amber-200",
  confirmed: "bg-green-50 text-green-700 border-green-200",
  completed: "bg-slate-100 text-slate-600 border-slate-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
};
const STATUS_OPTIONS = ["new", "contacted", "confirmed", "completed", "cancelled"];

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);

  useEffect(() => {
    if (!token) { setAuthed(false); return; }
    axios.get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => setAuthed(true))
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setToken(""); setAuthed(false); });
  }, [token]);

  if (!authed) return <Login onLogin={(t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); }} />;
  return <Dashboard token={token} onLogout={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); }} />;
}

function Login({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      onLogin(data.token);
      toast.success("Welcome back");
    } catch {
      toast.error("Invalid password");
    } finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-slate-50 grid place-items-center p-6">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-md bg-white rounded-3xl border border-slate-200 shadow-xl p-8">
        <div className="text-center mb-6">
          <div className="text-xs font-semibold tracking-[0.18em] uppercase text-[#081537] mb-2">Move On Admin</div>
          <h1 className="font-[Outfit] text-2xl font-bold text-[#081537]">Sign in to dashboard</h1>
        </div>
        <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Admin Password</Label>
        <Input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter admin password" autoFocus />
        <Button type="submit" disabled={loading || !password} data-testid="admin-login-btn" className="mt-6 w-full bg-[#081537] hover:bg-[#0F2152] rounded-full h-12 font-semibold">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing in...</> : "Sign in"}
        </Button>
      </form>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/bookings`, { headers });
      setBookings(data);
    } catch (e) {
      toast.error("Failed to load bookings");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  const updateStatus = async (bookingId, status) => {
    try {
      await axios.patch(`${API}/bookings/${bookingId}/status`, { status }, { headers });
      setBookings((s) => s.map((b) => b.booking_id === bookingId ? { ...b, status } : b));
      if (selected && selected.booking_id === bookingId) setSelected({ ...selected, status });
      toast.success(`Status updated to ${status}`);
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const filtered = bookings.filter((b) => {
    if (filter !== "all" && b.status !== filter) return false;
    if (search) {
      const s = search.toLowerCase();
      return b.name?.toLowerCase().includes(s) || b.mobile?.includes(s) || b.booking_id?.toLowerCase().includes(s) || b.pickup?.address?.toLowerCase().includes(s) || b.drop?.address?.toLowerCase().includes(s);
    }
    return true;
  });

  const counts = STATUS_OPTIONS.reduce((acc, s) => { acc[s] = bookings.filter((b) => b.status === s).length; return acc; }, {});

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <div className="text-xs font-semibold tracking-[0.18em] uppercase text-[#081537]">Move On Admin</div>
            <div className="font-[Outfit] text-lg font-bold text-[#081537]">Booking Dashboard</div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchAll} data-testid="admin-refresh-btn" className="rounded-full">
              <RefreshCcw className="w-4 h-4 mr-1.5" /> Refresh
            </Button>
            <Button variant="ghost" size="sm" onClick={onLogout} data-testid="admin-logout-btn" className="rounded-full text-slate-600">
              <LogOut className="w-4 h-4 mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-2 sm:grid-cols-5 gap-3">
        {STATUS_OPTIONS.map((s) => (
          <button key={s} onClick={() => setFilter(s === filter ? "all" : s)} data-testid={`stat-${s}`} className={cn("bg-white border rounded-2xl p-4 text-left transition", filter === s ? "border-[#081537] ring-2 ring-[#081537]/10" : "border-slate-200 hover:border-slate-300")}>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{s}</div>
            <div className="text-2xl font-bold text-[#081537] mt-1">{counts[s] || 0}</div>
          </button>
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8">
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="p-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center border-b border-slate-100">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search by name, mobile, ID, address..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="admin-search" />
            </div>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="sm:w-48" data-testid="admin-status-filter"><Filter className="w-4 h-4 mr-2" /><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="p-12 text-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-slate-500">No bookings found.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {filtered.map((b) => (
                <li key={b.booking_id} data-testid={`booking-row-${b.booking_id}`} className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 hover:bg-slate-50 cursor-pointer" onClick={() => setSelected(b)}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={cn("text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border font-semibold", STATUS_STYLES[b.status])}>{b.status}</span>
                      <span className="text-xs text-slate-400 font-medium">{b.booking_id}</span>
                    </div>
                    <div className="font-semibold text-[#081537] truncate">{b.name} · {b.shifting_type} shifting</div>
                    <div className="text-xs text-slate-500 truncate mt-0.5">{b.pickup?.address} → {b.drop?.address}</div>
                  </div>
                  <div className="flex sm:flex-col sm:items-end gap-3 sm:gap-1 text-xs text-slate-500">
                    <div className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {b.shifting_date}</div>
                    <div className="flex items-center gap-1"><Package className="w-3 h-3" /> {b.items?.length || 0} items</div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-300" />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {selected && <DetailDrawer booking={selected} onClose={() => setSelected(null)} onStatusChange={updateStatus} />}
    </div>
  );
}

function DetailDrawer({ booking, onClose, onStatusChange }) {
  const b = booking;
  const waLink = `https://wa.me/${b.mobile.replace(/\D/g, "")}?text=${encodeURIComponent(`Hi ${b.name}, this is Move On regarding your booking ${b.booking_id}.`)}`;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="booking-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white w-full max-w-lg h-full overflow-auto shadow-2xl">
        <div className="p-5 border-b border-slate-200 flex items-start justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="text-xs text-slate-500 font-semibold">{b.booking_id}</div>
            <div className="font-[Outfit] text-xl font-bold text-[#081537] mt-0.5">{b.name}</div>
            <div className="text-sm text-slate-500 capitalize">{b.shifting_type} shifting · {b.shifting_date}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-5 text-sm">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Status</div>
            <div className="flex gap-2 flex-wrap">
              {STATUS_OPTIONS.map((s) => (
                <button key={s} onClick={() => onStatusChange(b.booking_id, s)} data-testid={`change-status-${s}`} className={cn("px-3 py-1.5 rounded-full text-xs font-semibold border transition capitalize", b.status === s ? "bg-[#081537] text-white border-[#081537]" : "bg-white border-slate-200 text-slate-600 hover:border-[#081537]")}>{s}</button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <a href={waLink} target="_blank" rel="noreferrer" data-testid="drawer-whatsapp-btn" className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white rounded-lg px-4 py-2.5 font-semibold text-sm">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
            <a href={`tel:${b.mobile}`} className="flex items-center justify-center gap-2 bg-[#081537] hover:bg-[#0F2152] text-white rounded-lg px-4 py-2.5 font-semibold text-sm">
              <Phone className="w-4 h-4" /> Call
            </a>
          </div>

          <Section title="Contact">
            <Line icon={<Phone className="w-4 h-4" />} text={<><a href={`tel:${b.mobile}`} className="text-[#081537] font-medium">{b.mobile}</a> {b.whatsapp_preferred && <span className="ml-2 text-xs text-green-600 font-semibold">(WA preferred)</span>}</>} />
            <Line icon={<Mail className="w-4 h-4" />} text={<a href={`mailto:${b.email}`} className="text-[#081537] font-medium">{b.email}</a>} />
          </Section>

          <Section title="Locations">
            <Line icon={<MapPin className="w-4 h-4 text-[#081537]" />} text={<><strong>Pickup:</strong> {b.pickup?.address}<br /><span className="text-xs text-slate-500">Floor: {b.pickup?.floor || "-"}, Lift: {b.pickup?.lift || "-"}</span></>} />
            <Line icon={<MapPin className="w-4 h-4 text-red-600" />} text={<><strong>Drop:</strong> {b.drop?.address}<br /><span className="text-xs text-slate-500">Floor: {b.drop?.floor || "-"}, Lift: {b.drop?.lift || "-"}</span></>} />
            {b.distance_km && <div className="text-xs text-slate-500 pl-6">Distance: {b.distance_km} km · ~{Math.round(b.duration_min || 0)} min</div>}
          </Section>

          <Section title="Booking Details">
            <Line text={<><strong>Date:</strong> {b.shifting_date} {b.time_slot && `· ${b.time_slot}`}</>} />
            <Line text={<><strong>Vehicle:</strong> {b.vehicle_type || "N/A"}</>} />
          </Section>

          <Section title={`Items (${b.items?.length || 0})`}>
            {(b.items || []).length === 0 ? <div className="text-slate-500 text-sm">No items listed</div> :
              <ul className="space-y-1">
                {b.items.map((i, idx) => <li key={idx} className="text-sm text-slate-700 flex justify-between border-b border-slate-100 py-1"><span>{i.name} <span className="text-xs text-slate-400">({i.category})</span></span><span className="font-semibold">×{i.quantity}</span></li>)}
              </ul>
            }
          </Section>

          {b.description && (
            <Section title="Notes">
              <div className="text-slate-700 whitespace-pre-wrap">{b.description}</div>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-slate-200 rounded-xl p-4">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
function Line({ icon, text }) {
  return <div className="flex gap-2 items-start text-sm text-slate-700">{icon && <div className="mt-0.5">{icon}</div>}<div className="flex-1">{text}</div></div>;
}
